import { useState, useCallback, useEffect } from 'react';

/**
 * Type-safe hook for working with paginated data
 * Works with any paginated API that follows the pattern:
 * { items: T[], page: number, total_pages: number, total_count: number }
 */
export function usePagination<
  T,
  R extends {
    items: T[];
    page?: number;
    total_pages?: number;
    total_count?: number;
  },
>(
  queryHook: (args: any) => {
    data?: R;
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
    isSuccess: boolean;
    error?: any;
    refetch: () => void;
  },
  getQueryArgs: (page: number, limit: number) => any,
  initialPage = 1,
  initialPerPage = 20,
) {
  const [page, setPage] = useState(initialPage);
  const [perPage, setPerPage] = useState(initialPerPage);
  const [allItems, setAllItems] = useState<T[]>([]);

  // Get the current page of data
  const queryResult = queryHook(getQueryArgs(page, perPage));
  const { data, isSuccess, isFetching } = queryResult;

  // Update the accumulated items when new data arrives
  useEffect(() => {
    if (isSuccess && data?.items && !isFetching) {
      if (page === 1) {
        // Replace all items for first page
        setAllItems(data.items);
      } else {
        // Append items for subsequent pages
        setAllItems(prev => [...prev, ...data.items]);
      }
    }
  }, [data, isSuccess, isFetching, page]);

  // Calculate pagination state
  const totalCount = data?.total_count ?? 0;
  const totalPages = data?.total_pages ?? Math.ceil(totalCount / perPage);
  const hasNextPage = data ? page < totalPages : false;

  // Function to load more data
  const fetchNextPage = useCallback(() => {
    if (hasNextPage && !isFetching) {
      setPage(prev => prev + 1);
    }
  }, [hasNextPage, isFetching]);

  // Reset pagination
  const reset = useCallback(() => {
    setPage(1);
    setAllItems([]);
  }, []);

  return {
    // Original query result
    ...queryResult,

    // Enhanced data and state
    items: allItems,
    currentPageItems: data?.items ?? [],
    totalCount,
    totalPages,
    page,
    perPage,

    // Pagination actions
    hasNextPage,
    fetchNextPage,
    reset,
    setPage,
    setPerPage,
  };
}
